/*
 * ProductForm.tsx : Un formulaire pour ajouter ou modifier un produit.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	TextAreaField,
	ToggleButtonField,
	useFormDirty,
} from '../../../common';

// Import Service
import { ProductData, TaxRateOptions, UnitTypeOptions } from '../types/types';
import { productsApi } from '../services/productsApi';

// Composant ProductForm
const ProductForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<ProductData>({
		id: 0,
		label: '',
		description: '',
		sku: '',
		defaultPrice: 0,
		defaultTaxRate: {
			code: 'TVA_FR_20',
			label: 'TVA France 20%',
			rate: 0.2,
			country: 'FR',
			isActive: true,
		},
		unitType: {
			code: 'UNIT',
			label: 'Unité',
		},
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch du service et préremplissage du formulaire
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;

			productsApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion des changements dans le formulaire
	const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		// Si name est defaultTaxRate, on doit mettre à jour l'objet complet
		if (name === 'defaultTaxRate') {
			const selectedOption = TaxRateOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData({
					...formData,
					defaultTaxRate: {
						code: selectedOption.key,
						label: selectedOption.label,
						rate:
							selectedOption.key === 'TVA_FR_20'
								? 0.2
								: selectedOption.key === 'TVA_FR_10'
									? 0.1
									: 0.055,
						country: 'FR',
						isActive: true,
					},
				});
			}
			return;
		} else {
			// si name est unitType, on doit aussi mettre à jour l'objet complet
			if (name === 'unitType') {
				const selectedOption = UnitTypeOptions.find((opt) => opt.key === value);
				if (selectedOption) {
					setFormData({
						...formData,
						unitType: {
							code: selectedOption.key,
							label: selectedOption.label,
						},
					});
				}
				return;
			} else {
				// Pour les autres champs, mise à jour standard
				setFormData({ ...formData, [name]: value });
			}
		}
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/settings/produits');
		// Si un id est présent, mise à jour du produit
		if (id) {
			productsApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouveau produit
			productsApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(`/app/settings/products/${created.id}/edit`, {
											replace: true,
										})
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Rendu du composant
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<div className="flex items-start mb-3 space-x-4">
					{/* Icône à gauche */}
					<FontAwesomeIcon
						icon={faLayerGroup}
						className="w-20 h-20 text-blue-900/90"
					/>

					{/* Titre + toggle à droite */}
					<div className="flex flex-col mt-3">
						<h1 className="text-3xl font-bold">
							{id ? "Édition d'un produit" : 'Nouveau produit'}
						</h1>
					</div>
				</div>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Label"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : Hors Forfait Salle"
								/>
								<InputField
									label="SKU"
									name="sku"
									value={formData.sku}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 00125948"
								/>
								<TextAreaField
									label="Description"
									name="description"
									value={formData.description}
									onChange={handleChangeTextArea}
									required
									loading={loading}
									placeholder="ex : Service pour l'utilisation des salles de réunion"
								/>
								<ToggleButtonField
									label="En vente"
									isOn={formData.forSale ? true : false}
									onChange={(checked) =>
										setFormData({ ...formData, forSale: checked })
									}
									loading={loading}
								/>
								<ToggleButtonField
									label="En achat"
									isOn={formData.forPurchase ? true : false}
									onChange={(checked) =>
										setFormData({ ...formData, forPurchase: checked })
									}
									loading={loading}
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<InputField
									label="Prix par défaut (€ HT)"
									name="defaultPrice"
									value={formData.defaultPrice}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 600"
								/>
								<SelectField
									label="Taxe par défaut"
									selectedKey={formData.defaultTaxRate?.code}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'defaultTaxRate',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={TaxRateOptions}
									entityHref={
										formData.defaultTaxRate?.id
											? `/app/taxrates/${formData.defaultTaxRate.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											defaultTaxRate: null,
										}))
									}
								/>
								<SelectField
									label="Type d'unité"
									selectedKey={formData.unitType?.code}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'unitType',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={UnitTypeOptions}
								/>
							</div>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate('/app/settings/produits')}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default ProductForm;
